import React, { useMemo, useState } from 'react';
import { TrendingUp, DollarSign, Activity, AlertCircle, ChevronDown, Check, Calendar, Zap } from 'lucide-react';
import { AppCard } from '../components/base/AppCard';
import { AppButton } from '../components/base/AppButton';
import { AppInput } from '../components/base/AppInput';
import { AppBadge } from '../components/base/AppBadge';
import { Toast } from '../components/Toast';
import { mockAgents, mockCostData, mockHourlyActivity } from '../data/mockData';
type TabType = 'consumption' | 'limits';
interface ServiceLimit {
  serviceId: string;
  serviceName: string;
  dailyLimit: number;
  monthlyLimit: number;
  currentDailyUsage: number;
  currentMonthlyUsage: number;
}
export function ConsumiPage() {
  const [activeTab, setActiveTab] = useState<TabType>('consumption');
  const [selectedServiceId, setSelectedServiceId] = useState<string>('1');
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);
  // Mock service limits data
  const [serviceLimits, setServiceLimits] = useState<ServiceLimit[]>([{
    serviceId: '1',
    serviceName: 'Service 1',
    dailyLimit: 100,
    monthlyLimit: 2000,
    currentDailyUsage: 45,
    currentMonthlyUsage: 850
  }, {
    serviceId: '2',
    serviceName: 'Service 2',
    dailyLimit: 150,
    monthlyLimit: 3000,
    currentDailyUsage: 78,
    currentMonthlyUsage: 1250
  }, {
    serviceId: '3',
    serviceName: 'Service 3',
    dailyLimit: 200,
    monthlyLimit: 4000,
    currentDailyUsage: 120,
    currentMonthlyUsage: 1800
  }]);
  const selectedService = mockAgents.find(a => a.id === selectedServiceId);
  const selectedServiceCost = mockCostData.costPerAgent.find(c => c.agentId === selectedServiceId);
  const selectedServiceLimit = serviceLimits.find(l => l.serviceId === selectedServiceId);
  // Calculate consumption metrics
  const totalRequests = useMemo(() => {
    return mockHourlyActivity.reduce((sum, h) => sum + h.calls, 0);
  }, []);
  const avgRequestsPerHour = useMemo(() => {
    return Math.round(totalRequests / mockHourlyActivity.length);
  }, [totalRequests]);
  const peakHour = useMemo(() => {
    const peak = mockHourlyActivity.reduce((max, h) => h.calls > max.calls ? h : max);
    return peak.hour;
  }, []);
  const handleServiceSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setIsServiceDropdownOpen(false);
  };
  const handleUpdateLimit = (serviceId: string, type: 'daily' | 'monthly', value: number) => {
    setServiceLimits(prev => prev.map(limit => limit.serviceId === serviceId ? {
      ...limit,
      [type === 'daily' ? 'dailyLimit' : 'monthlyLimit']: value
    } : limit));
    setToast({
      message: 'Limite aggiornato con successo',
      type: 'success'
    });
  };
  const getUsagePercentage = (current: number, limit: number) => {
    return Math.min(current / limit * 100, 100);
  };
  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600 dark:text-red-400';
    if (percentage >= 70) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
  };
  const tabs = [{
    id: 'consumption' as TabType,
    label: 'Consumi'
  }, {
    id: 'limits' as TabType,
    label: 'Limiti'
  }];
  return <div className="pb-12 space-y-6" data-id="element-1124">
      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} isVisible={!!toast} onClose={() => setToast(null)} data-id="element-1125" />}

      {/* Header with Service Selector */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-1126">
        <div data-id="element-1127">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2" data-id="element-1128">
            Consumi Service
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1129">
            Monitora l'utilizzo e imposta limiti di consumo
          </p>
        </div>

        {/* Service Selector */}
        <div className="relative min-w-[200px]" data-id="element-1130">
          

          {isServiceDropdownOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl overflow-hidden z-50 shadow-lg" data-id="element-1131">
              {mockAgents.map(service => <button key={service.id} onClick={() => handleServiceSelect(service.id)} className={`w-full px-4 py-2.5 text-left text-sm font-medium transition-colors duration-150 flex items-center justify-between ${selectedServiceId === service.id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-hover'}`} data-id="element-1132">
                  <span data-id="element-1133">{service.name}</span>
                  {selectedServiceId === service.id && <Check className="w-4 h-4" data-id="element-1134" />}
                </button>)}
            </div>}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-dark-subtle" data-id="element-1135">
        <nav className="flex gap-8" data-id="element-1136">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-1137">
              {tab.label}
            </button>)}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'consumption' && <div className="space-y-6" data-id="element-1138">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-1139">
            <AppCard data-id="element-1140">
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1141">
                €{selectedServiceCost?.totalCostEur.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1142">
                Costo mensile
              </p>
            </AppCard>

            <AppCard data-id="element-1143">
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1144">
                {totalRequests}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1145">
                Richieste totali
              </p>
            </AppCard>

            <AppCard data-id="element-1146">
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1147">
                {avgRequestsPerHour}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1148">
                Media richieste/ora
              </p>
            </AppCard>

            <AppCard data-id="element-1149">
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-1150">
                {peakHour}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1151">
                Ora di picco
              </p>
            </AppCard>
          </div>

          {/* Hourly Activity Chart */}
          <AppCard data-id="element-1152">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1153">
              Attività nelle ultime 24 ore
            </h2>

            <div className="relative h-64" data-id="element-1154">
              {/* Y-axis */}
              <div className="absolute left-0 top-0 bottom-8 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pr-2" data-id="element-1155">
                <span data-id="element-1156">100</span>
                <span data-id="element-1157">75</span>
                <span data-id="element-1158">50</span>
                <span data-id="element-1159">25</span>
                <span data-id="element-1160">0</span>
              </div>

              {/* Chart */}
              <div className="ml-8 absolute top-0 bottom-8 left-0 right-0 flex items-end justify-between gap-0.5" data-id="element-1161">
                {mockHourlyActivity.map(data => {
              const heightPercent = data.calls / 100 * 100;
              return <div key={data.hour} className="flex-1 flex flex-col justify-end group relative h-full" data-id="element-1162">
                      <div className="w-full rounded-t bg-blue-500 dark:bg-blue-600 hover:bg-blue-600 dark:hover:bg-blue-500 cursor-pointer transition-all" style={{
                  height: `${heightPercent}%`,
                  minHeight: '2px'
                }} data-id="element-1163">
                        <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10" data-id="element-1164">
                          {data.hour}: {data.calls} richieste
                        </div>
                      </div>
                    </div>;
            })}
              </div>

              {/* X-axis */}
              <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-1165">
                {mockHourlyActivity.filter((_, idx) => idx % 4 === 0).map(data => <span key={data.hour} className="flex-1 text-center" data-id="element-1166">
                      {data.hour}
                    </span>)}
              </div>
            </div>
          </AppCard>

          {/* Current Usage Status */}
          {selectedServiceLimit && <AppCard data-id="element-1167">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1168">
                Stato utilizzo corrente
              </h2>

              <div className="space-y-6" data-id="element-1169">
                {/* Daily Usage */}
                <div data-id="element-1170">
                  <div className="flex items-center justify-between mb-2" data-id="element-1171">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1172">
                      Utilizzo giornaliero
                    </span>
                    <span className={`text-sm font-semibold ${getUsageColor(getUsagePercentage(selectedServiceLimit.currentDailyUsage, selectedServiceLimit.dailyLimit))}`} data-id="element-1173">
                      {selectedServiceLimit.currentDailyUsage} /{' '}
                      {selectedServiceLimit.dailyLimit} richieste
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-medium rounded-full h-2" data-id="element-1174">
                    <div className={`h-2 rounded-full transition-all ${getUsagePercentage(selectedServiceLimit.currentDailyUsage, selectedServiceLimit.dailyLimit) >= 90 ? 'bg-red-500' : getUsagePercentage(selectedServiceLimit.currentDailyUsage, selectedServiceLimit.dailyLimit) >= 70 ? 'bg-amber-500' : 'bg-green-500'}`} style={{
                width: `${getUsagePercentage(selectedServiceLimit.currentDailyUsage, selectedServiceLimit.dailyLimit)}%`
              }} data-id="element-1175" />
                  </div>
                </div>

                {/* Monthly Usage */}
                <div data-id="element-1176">
                  <div className="flex items-center justify-between mb-2" data-id="element-1177">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1178">
                      Utilizzo mensile
                    </span>
                    <span className={`text-sm font-semibold ${getUsageColor(getUsagePercentage(selectedServiceLimit.currentMonthlyUsage, selectedServiceLimit.monthlyLimit))}`} data-id="element-1179">
                      {selectedServiceLimit.currentMonthlyUsage} /{' '}
                      {selectedServiceLimit.monthlyLimit} richieste
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-medium rounded-full h-2" data-id="element-1180">
                    <div className={`h-2 rounded-full transition-all ${getUsagePercentage(selectedServiceLimit.currentMonthlyUsage, selectedServiceLimit.monthlyLimit) >= 90 ? 'bg-red-500' : getUsagePercentage(selectedServiceLimit.currentMonthlyUsage, selectedServiceLimit.monthlyLimit) >= 70 ? 'bg-amber-500' : 'bg-green-500'}`} style={{
                width: `${getUsagePercentage(selectedServiceLimit.currentMonthlyUsage, selectedServiceLimit.monthlyLimit)}%`
              }} data-id="element-1181" />
                  </div>
                </div>
              </div>
            </AppCard>}
        </div>}

      {activeTab === 'limits' && <div className="space-y-6" data-id="element-1182">
          {/* Info Banner */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl" data-id="element-1183">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-1184" />
            <div data-id="element-1185">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100" data-id="element-1186">
                Gestione limiti di consumo
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1" data-id="element-1187">
                Imposta limiti giornalieri e mensili per controllare l'utilizzo
                del service. Riceverai notifiche quando raggiungi il 90% del
                limite.
              </p>
            </div>
          </div>

          {/* Service Limits - Only Selected Service */}
          {selectedServiceLimit && <AppCard data-id="element-1188">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1189">
                Limiti per {selectedServiceLimit.serviceName}
              </h2>

              <div className="p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl border border-gray-200 dark:border-dark-medium" data-id="element-1190">
                <div className="flex items-center justify-between mb-4" data-id="element-1191">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100" data-id="element-1192">
                    {selectedServiceLimit.serviceName}
                  </h3>
                  <AppBadge variant="blue" data-id="element-1193">
                    {Math.round(selectedServiceLimit.currentMonthlyUsage / selectedServiceLimit.monthlyLimit * 100)}
                    % utilizzato
                  </AppBadge>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1194">
                  {/* Daily Limit */}
                  <div data-id="element-1195">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1196">
                      Limite giornaliero (richieste)
                    </label>
                    <AppInput type="number" value={selectedServiceLimit.dailyLimit.toString()} onChange={e => handleUpdateLimit(selectedServiceLimit.serviceId, 'daily', parseInt(e.target.value) || 0)} data-id="element-1197" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1198">
                      Utilizzo corrente:{' '}
                      {selectedServiceLimit.currentDailyUsage} richieste
                    </p>
                  </div>

                  {/* Monthly Limit */}
                  <div data-id="element-1199">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1200">
                      Limite mensile (richieste)
                    </label>
                    <AppInput type="number" value={selectedServiceLimit.monthlyLimit.toString()} onChange={e => handleUpdateLimit(selectedServiceLimit.serviceId, 'monthly', parseInt(e.target.value) || 0)} data-id="element-1201" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1202">
                      Utilizzo corrente:{' '}
                      {selectedServiceLimit.currentMonthlyUsage} richieste
                    </p>
                  </div>
                </div>
              </div>
            </AppCard>}

          {/* Global Settings */}
          <AppCard data-id="element-1203">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-6" data-id="element-1204">
              Impostazioni globali
            </h2>

            <div className="space-y-4" data-id="element-1205">
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl" data-id="element-1206">
                <div data-id="element-1207">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1208">
                    Notifiche di avviso
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1209">
                    Ricevi notifiche al 70% e 90% del limite
                  </p>
                </div>
                <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1210" />
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-subtle rounded-xl" data-id="element-1211">
                <div data-id="element-1212">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-1213">
                    Blocco automatico
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-1214">
                    Blocca automaticamente il service al raggiungimento del
                    limite
                  </p>
                </div>
                <input type="checkbox" className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1215" />
              </div>
            </div>
          </AppCard>
        </div>}
    </div>;
}